// FlashOrderView.js
// Customer-facing flash order screen — sticker sheet character picker + color selector.
// Rendered by CustomerOrderView when ticketType === "flash".
// Handles full flash submission lifecycle: design pick → form → Firestore write → done.
//
// Props:
//   tokenId    (string)  — the token being redeemed
//   tokenData  (object)  — token doc data (for squareOrderRef)
//   onConfirmed(orderId, queueNumber) — called on successful submit
//   onQueueClosed()                   — called when no active shift at submit time
//   onError(message)                  — called on unrecoverable Firestore error

import React, { useState, useRef, useEffect } from 'react';
import { db } from './firebase';
import { resolveShift } from './utils/shiftScope';
import {
  doc, getDoc, addDoc, collection,
  updateDoc, getDocs, query, where,
  onSnapshot,
  runTransaction, serverTimestamp,
} from 'firebase/firestore';
import useColorPalette from './hooks/useColorPalette';
import useFlashDesigns from './hooks/useFlashDesigns';

const BRAND = {
  gold:   '#fcb31f',
  dark:   '#13449b',
  cream:  '#ffdaad',
  blue:   '#1f59db',
  sky:    '#4aa4e1',
  pink:   '#ef537e',
  orange: '#f57600',
};

// Replicates the same atomic counter increment used by the custom order flow
async function assignQueueNumber(shiftId) {
  const counterSnap = await getDocs(collection(db, 'shifts', shiftId, 'meta'));
  if (counterSnap.empty) return null;
  const counterRef = counterSnap.docs[0].ref;
  return runTransaction(db, async (tx) => {
    const counterDoc = await tx.get(counterRef);
    if (!counterDoc.exists()) throw new Error('Counter document missing');
    const next = (counterDoc.data().lastNumber ?? 0) + 1;
    tx.update(counterRef, { lastNumber: next });
    return next;
  });
}

// Per-design lifetime counter. Stamps each flash/freestyle order with the
// nth time that character (or freestyle) has been drawn. Writes to a single
// shared doc (config/designCounters) keyed by designId (or "freestyle"),
// atomically via a Firestore transaction. Non-fatal on failure — the order
// still submits, it just won't have a sequence number.
async function assignDesignSequence(key) {
  try {
    const counterRef = doc(db, 'config', 'designCounters');
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const data = snap.exists() ? snap.data() : {};
      const next = (data[key] || 0) + 1;
      tx.set(counterRef, { [key]: next }, { merge: true });
      return next;
    });
  } catch (err) {
    console.warn('[GTS] designSequence assign failed:', err);
    return null;
  }
}

// ── STAR DECORATION ──────────────────────────────────────────────────────────
// Scattered absolutely over the two-column grid. Only star-shaped silhouettes —
// no emoji (✨ renders as a square on some platforms).
const SCATTERED_STARS = [
  { x: '46%', y:  '3%', size: '11px', delay: 0.1 },
  { x: '51%', y:  '9%', size:  '8px', delay: 0.5 },
  { x: '44%', y: '18%', size: '13px', delay: 0.3 },
  { x: '53%', y: '24%', size:  '9px', delay: 0.8 },
  { x: '47%', y: '33%', size: '10px', delay: 0.2 },
  { x: '50%', y: '40%', size: '12px', delay: 0.6 },
  { x: '45%', y: '50%', size:  '8px', delay: 0.0 },
  { x: '54%', y: '57%', size: '11px', delay: 0.4 },
  { x: '48%', y: '66%', size:  '9px', delay: 0.7 },
  { x: '52%', y: '73%', size: '13px', delay: 0.2 },
  { x: '46%', y: '82%', size: '10px', delay: 0.5 },
  { x: '50%', y: '90%', size:  '8px', delay: 0.1 },
];
const STAR_CHARS = ['★', '✦', '✧', '✭', '✯', '✰', '★', '✦', '✧', '✭', '✯', '✰'];

// ── SPEECH BUBBLE ────────────────────────────────────────────────────────────
function QuoteBubble({ text, delay = 0, tailAlign = '50%' }) {
  return (
    <div style={{
      background: '#ffdaad',
      borderRadius: '12px',
      padding: '0.45rem 0.6rem',
      position: 'relative',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      animation: `gtsBubbleGrow 0.35s ${delay}s cubic-bezier(0.34,1.4,0.64,1) both`,
    }}>
      <div style={{ color: '#1d52a0', fontSize: '0.83rem', fontStyle: 'italic', fontWeight: '600', textAlign: 'center', lineHeight: 1.35 }}>
        "{text}"
      </div>
      <div style={{
        position: 'absolute', bottom: '-7px', left: tailAlign, transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '7px solid transparent', borderRight: '7px solid transparent',
        borderTop: '7px solid #ffdaad',
      }} />
    </div>
  );
}

// ── CHARACTER STICKER ─────────────────────────────────────────────────────────
// PNG on transparent background — gold glow follows the actual shape.
function CharacterSticker({ design, selected, onSelect, marginLeft, size = 129 }) {
  const isSplit = design.quote?.includes('|');
  const quotes  = isSplit ? design.quote.split('|') : null;
  return (
    <div
      onClick={() => onSelect(design)}
      style={{
        marginLeft,
        width: `${size + 40}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* Speech bubble(s) — appear above character on select */}
      {selected && design.quote && (
        isSplit ? (
          /* Two bubbles for dual characters like SunnynSugar */
          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', maxWidth: `${size + 50}px` }}>
            <QuoteBubble text={quotes[0]} delay={0} tailAlign="65%" />
            <QuoteBubble text={quotes[1]} delay={0.12} tailAlign="35%" />
          </div>
        ) : (
          <div style={{ marginBottom: '6px', maxWidth: `${size + 36}px` }}>
            <QuoteBubble text={design.quote} />
          </div>
        )
      )}
      <div style={{ animation: selected ? 'gtsCharBounce 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards' : 'none' }}>
        <img
          src={design.imageUrl}
          alt={design.label}
          draggable={false}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            objectFit: 'contain',
            filter: selected
              ? 'drop-shadow(0 0 7px rgba(252,179,31,0.95)) drop-shadow(0 0 18px rgba(252,179,31,0.55))'
              : 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
            animation: selected ? 'gts-char-pulse 2s ease-in-out infinite' : 'none',
            transition: 'filter 0.2s ease',
          }}
        />
      </div>
      <span style={{
        marginTop: '5px',
        fontSize: '0.92rem',
        fontFamily: "'Fredoka One', 'Fredoka', cursive",
        color: selected ? BRAND.gold : '#888',
        transition: 'color 0.2s ease',
        textAlign: 'center',
      }}>
        {design.label}
      </span>
      {selected && design.subtitle && (
        <span style={{
          fontSize: '0.75rem',
          color: 'rgba(255,218,173,0.7)',
          textAlign: 'center',
          fontStyle: 'italic',
          lineHeight: 1.3,
          paddingRight: '0.15em',
          marginTop: '1px',
          animation: 'gtsBubbleGrow 0.3s 0.1s cubic-bezier(0.34,1.4,0.64,1) both',
        }}>
          {design.subtitle}
        </span>
      )}
    </div>
  );
}

// ── COLOR SWATCH ──────────────────────────────────────────────────────────────
// Black/white need a visible resting border so they don't disappear into the background.

function ColorSwatch({ color, selected, onToggle, dimmed }) {
  const restingBorder = '2px solid rgba(255,255,255,0.35)';
  return (
    <button
      onClick={() => onToggle(color.id)}
      aria-label={color.label}
      style={{
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: color.hex,
        border: selected ? `3px solid ${BRAND.gold}` : restingBorder,
        cursor: dimmed ? 'not-allowed' : 'pointer',
        padding: 0,
        transition: 'transform 0.15s ease, filter 0.2s ease, opacity 0.2s ease',
        transform: selected ? 'scale(1.12)' : 'scale(1)',
        filter: selected
          ? 'drop-shadow(0 0 5px rgba(252,179,31,0.9)) drop-shadow(0 0 12px rgba(252,179,31,0.5))'
          : 'none',
        opacity: dimmed ? 0.4 : 1,
        animation: selected ? 'gts-color-pulse 2s ease-in-out infinite' : 'none',
      }}
    />
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function FlashOrderView({ tokenId, tokenData, onConfirmed, onQueueClosed, onError, onSwitchToCustom }) {
  const COLOR_OPTIONS = useColorPalette();
  // Active shift drives flash design profile resolution. If no shift is open
  // the customer screen falls back to base designs, but realistically a
  // queue-closed screen will short-circuit before this matters.
  //
  // Scoped to the guest's own booth (2026-09-25): the ticket in their hand
  // names the shift it was issued on, so a second concurrent event can't
  // swap in its flash board — or, below, its queue counter.
  const scopeShiftId = tokenData?.shiftId || null;
  const [activeShift, setActiveShift] = useState(null);
  useEffect(() => {
    const q = query(collection(db, 'shifts'), where('isActive', '==', true));
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Their shift missing from the live set means their booth closed —
      // never substitute the other venue's.
      setActiveShift(scopeShiftId
        ? (all.find(x => x.id === scopeShiftId) || null)
        : resolveShift(all).shift);
    });
  }, [scopeShiftId]);
  const { allDesigns } = useFlashDesigns(activeShift);
  const [selectedDesign,      setSelectedDesign]      = useState(null);
  const [selectedColors,      setSelectedColors]      = useState([]);
  const [freestyleDescription,setFreestyleDescription]= useState('');
  const [customerName,        setCustomerName]        = useState('');
  const [recipientName,       setRecipientName]       = useState('');
  const [instructions,        setInstructions]        = useState('');
  const [disclaimerChecked,   setDisclaimerChecked]   = useState(false);
  const [submitting,          setSubmitting]          = useState(false);

  const formRef = useRef(null);

  const isFreestyle = selectedDesign?.id === 'freestyle';
  const canSubmit   = !submitting &&
    selectedDesign &&
    customerName.trim() &&
    (isFreestyle ? disclaimerChecked : true) &&
    (!isFreestyle || freestyleDescription.trim());

  // No auto-scroll — let the user browse the characters and scroll naturally

  function handleSelectDesign(design) {
    setSelectedDesign(design);
    // Reset freestyle description when switching away from freestyle
    if (design.id !== 'freestyle') setFreestyleDescription('');
  }

  function handleToggleColor(colorId) {
    setSelectedColors(prev => {
      if (prev.includes(colorId)) return prev.filter(c => c !== colorId);
      if (prev.length >= 3) return prev; // max 3 — just ignore the tap
      return [...prev, colorId];
    });
  }

  async function handleSubmit() {
    if (!canSubmit) return;

    const shiftCheck = await getDocs(query(collection(db, 'shifts'), where('isActive', '==', true)));
    const shiftDocs  = shiftCheck.docs.map(d => ({ id: d.id, ...d.data() }));
    const submitShift = scopeShiftId
      ? (shiftDocs.find(x => x.id === scopeShiftId) || null)
      : resolveShift(shiftDocs).shift;
    if (!submitShift) { onQueueClosed(); return; }

    setSubmitting(true);

    try {
      // Re-read token — bail if already used (race condition guard)
      const freshToken = await getDoc(doc(db, 'tokens', tokenId));
      if (!freshToken.exists() || freshToken.data().status !== 'issued') {
        onError('This ticket has already been used or is no longer valid.');
        return;
      }

      // Get queue number from the guest's OWN shift — never docs[0], which
      // would draw from whichever concurrent booth Firestore returned first.
      const shiftId        = submitShift.id;
      // Inherit the test flag from the shift — keeps Lab-driven orders inside
      // the _isTestData seal (purge sweep, public builders, auto-emails).
      const shiftIsTest    = submitShift._isTestData === true;
      const assignedNumber = await assignQueueNumber(shiftId);

      // Per-design lifetime sequence — powers auto-naming ("Saturnina (#6)" /
      // "Freestyle (#7) — Fire Truck"). Key is "freestyle" for freestyle
      // orders, otherwise the design id.
      const sequenceKey  = isFreestyle ? 'freestyle' : selectedDesign.id;
      const designSequence = await assignDesignSequence(sequenceKey);

      const orderDoc = {
        tokenId,
        shiftId,
        ...(shiftIsTest ? { _isTestData: true } : {}),
        orderType:              isFreestyle ? 'freestyle_flash' : 'flash',
        state:                  'queued',
        queueNumber:            assignedNumber,
        customerName:           customerName.trim(),
        recipientName:          recipientName.trim() || null,
        designId:               selectedDesign.id,
        designLabel:            selectedDesign.label,
        designSequence,
        isFreestyle,
        favoriteColors:         selectedColors,
        notes:                  isFreestyle
                                  ? freestyleDescription.trim()
                                  : (instructions.trim() || null),
        freestyleDescription:   isFreestyle ? freestyleDescription.trim() : null,
        subjectName:            null,
        imageUrl:               null,
        croppedImageUrl:        null,
        submittedAt:            serverTimestamp(),
        originalQueuePosition:  assignedNumber,
        suspensionCount:        0,
        portalEnabled:          false,
        portal_state:           'active',
        nft_status:             'none',
        photo_url:              null,
        photo_cutout_url:       null,
        customer_email:         null,
        email_marketing_opt_in: false,
        ipfs_url:               null,
        event_name:             null,
        event_location:         null,
        keepsake_expires_at:    null,
        startedAt:              null,
        flippedAt:              null,
        completedAt:            null,
        rejectedAt:             null,
        suspendedAt:            null,
        resubmittedAt:          null,
        drawingTimeMs:          null,
        cookTimeMs:             null,
        framingDrawnWidthFrac:  null,
        framingDrawnHeightFrac: null,
        framingOffsetXFrac:     null,
        framingOffsetYFrac:     null,
        squareOrderRef:         tokenData?.squareOrderId ?? null,
      };

      // Mark token used before writing order — no orphaned orders on failure
      await updateDoc(doc(db, 'tokens', tokenId), { status: 'used', usedAt: serverTimestamp() });
      const orderRef = await addDoc(collection(db, 'orders'), orderDoc);

      // Backfill orderId onto token — enables portal lookup when customer re-scans QR later
      updateDoc(doc(db, 'tokens', tokenId), { orderId: orderRef.id }).catch(() => {});

      onConfirmed(orderRef.id, assignedNumber);

    } catch (err) {
      console.error('Flash order submission error:', err);
      setSubmitting(false);
      onError('Something went wrong submitting your order. Please try again.');
    }
  }

  // All designs including freestyle in one flat list for the grid
  const ALL_DESIGNS = allDesigns;
  // Left column: indices 0, 2, 4 — Right column: indices 1, 3, 5 (offset down)
  const leftCol  = ALL_DESIGNS.filter((_, i) => i % 2 === 0);
  const rightCol = ALL_DESIGNS.filter((_, i) => i % 2 === 1);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* CSS + Google Font — injected once, scoped to gts- prefix */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&display=swap');
        @keyframes gtsBubbleGrow {
          0%   { opacity: 0; transform: scale(0.4); }
          60%  { opacity: 1; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes gtsCharBounce {
          0%   { transform: scale(1); }
          30%  { transform: scale(1.12); }
          50%  { transform: scale(0.97); }
          70%  { transform: scale(1.04); }
          100% { transform: scale(1.05); }
        }
        @keyframes gts-twinkle {
          0%, 100% { opacity: 0.25; transform: scale(0.8); }
          50%       { opacity: 1;    transform: scale(1.2); }
        }
        @keyframes gts-char-pulse {
          0%, 100% { filter: drop-shadow(0 0 7px rgba(252,179,31,0.95)) drop-shadow(0 0 18px rgba(252,179,31,0.55)); }
          50%       { filter: drop-shadow(0 0 10px rgba(252,179,31,1))   drop-shadow(0 0 28px rgba(252,179,31,0.75)); }
        }
        @keyframes gts-color-pulse {
          0%, 100% { filter: drop-shadow(0 0 5px rgba(252,179,31,0.9))  drop-shadow(0 0 12px rgba(252,179,31,0.5)); }
          50%       { filter: drop-shadow(0 0 8px rgba(252,179,31,1))   drop-shadow(0 0 20px rgba(252,179,31,0.7)); }
        }
      `}</style>

      {/* ── Prompt ── */}
      <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: 1.5, textAlign: 'center' }}>
        Tap a character to begin.
      </p>

      {/* ── Sticker sheet — two columns, right column offset down.
          Capped at 55vh with internal scroll so the "Bring your own photo"
          button below stays within a finger's reach on any device without
          scrolling past six stickers first. ── */}
      <div style={{
        position: 'relative',
        maxHeight: selectedDesign ? 'none' : '40vh',
        overflowY: selectedDesign ? 'visible' : 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        marginBottom: '0.5rem',
        // Subtle fade on the bottom edge to hint more content scrolls below
        maskImage: selectedDesign ? 'none' : 'linear-gradient(to bottom, #000 0%, #000 92%, rgba(0,0,0,0.25) 100%)',
        WebkitMaskImage: selectedDesign ? 'none' : 'linear-gradient(to bottom, #000 0%, #000 92%, rgba(0,0,0,0.25) 100%)',
      }}>
      <div style={{ position: 'relative', display: 'flex', gap: '8px', paddingBottom: selectedDesign ? 0 : '0.75rem' }}>

        {/* Scattered stars in the gutter between columns */}
        {SCATTERED_STARS.map((star, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: star.x,
              top: star.y,
              fontSize: star.size,
              color: BRAND.gold,
              opacity: 0.55,
              animation: `gts-twinkle ${1.6 + i * 0.22}s ease-in-out ${star.delay}s infinite`,
              display: 'inline-block',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {STAR_CHARS[i % STAR_CHARS.length]}
          </span>
        ))}

        {/* Left column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {leftCol.map(design => (
            <CharacterSticker
              key={design.id}
              design={design}
              selected={selectedDesign?.id === design.id}
              onSelect={handleSelectDesign}
              marginLeft="0"
            />
          ))}
        </div>

        {/* Right column — offset down by 48px */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', paddingTop: '48px' }}>
          {rightCol.map(design => (
            <CharacterSticker
              key={design.id}
              design={design}
              selected={selectedDesign?.id === design.id}
              onSelect={handleSelectDesign}
              marginLeft="0"
            />
          ))}
        </div>

      </div>
      </div>

      {/* ── Order form — appears when a design is selected ── */}
      {selectedDesign && (
        <div ref={formRef} style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(74,164,225,0.4)' }}>

          {/* Selected design header */}
          <h3 style={{ color: BRAND.gold, margin: '0 0 0.25rem 0', fontSize: '1.25rem', textAlign: 'center' }}>
            ✦ {isFreestyle ? 'Freestyle Request' : selectedDesign.label}{selectedDesign.subtitle && !isFreestyle ? `, ${selectedDesign.subtitle}` : ''} ✦
          </h3>
          {!isFreestyle && (
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.92rem', marginBottom: '1.25rem', textAlign: 'center' }}>
              Tap a different character above to change your selection.
            </p>
          )}

          {/* Freestyle description */}
          {isFreestyle && (
            <>
              <p style={{ color: BRAND.cream, fontSize: '1rem', lineHeight: 1.5, margin: '0.5rem 0 1rem' }}>
                Dan draws from memory — no reference photo needed, no promises made.
                Characters, animals, portraits, strange requests — all welcome.{' '}
                <em>Mistakes are delicious.</em>
              </p>
              <label style={labelStyle}>What should Dan draw?</label>
              <input
                style={inputStyle}
                type="text"
                placeholder="e.g. a golden retriever, Darth Vader, a sunflower…"
                value={freestyleDescription}
                onChange={e => setFreestyleDescription(e.target.value)}
                maxLength={30}
              />
              <div style={counterStyle(30, freestyleDescription.length)}>
                {freestyleDescription.length}/30
              </div>
            </>
          )}

          {/* Favorite colors */}
          <label style={{ ...labelStyle, marginTop: isFreestyle ? '1.25rem' : 0 }}>
            Favorite colors <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 'normal' }}>(pick up to 3)</span>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 60px)', gap: '12px', margin: '0.75rem 0 1.5rem' }}>
            {COLOR_OPTIONS.map(color => (
              <ColorSwatch
                key={color.id}
                color={color}
                selected={selectedColors.includes(color.id)}
                onToggle={handleToggleColor}
                dimmed={selectedColors.length >= 3 && !selectedColors.includes(color.id)}
              />
            ))}
          </div>

          {/* Callout name — what the artist shouts when it's ready. Helper
              text under the field makes its purpose obvious so guests stop
              writing their pet's name or "me" here. */}
          <label style={labelStyle}>Your first name</label>
          <input
            style={inputStyle}
            type="text"
            placeholder="First name"
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            maxLength={40}
          />
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginTop: '0.25rem', lineHeight: 1.4 }}>
            So we can call you over when your pancake is ready.
          </div>
          <div style={counterStyle(40, customerName.length)}>
            {customerName.length}/40
          </div>

          {/* Gift recipient removed — flash designs are already named by the
              design + sequence number (e.g. "Saturnina (#7)"). The "who's this
              for" field confused guests into writing "me" or their pet's name. */}

          {/* A note — inspiration, suggestion, context. Same placeholder copy
              as the custom-order form so the guest-facing vocabulary is
              consistent across flows. */}
          {!isFreestyle && (
            <>
              <label style={{ ...labelStyle, marginTop: '1rem' }}>
                A note <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 'normal' }}>(optional)</span>
              </label>
              <input
                style={inputStyle}
                type="text"
                placeholder="a note, a suggestion, a bit of inspiration…"
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                maxLength={120}
              />
              <div style={counterStyle(120, instructions.length)}>
                {instructions.length}/120
              </div>
            </>
          )}

          {/* Disclaimer — only for freestyle where the guest is typing a custom request */}
          {isFreestyle && (
            <div style={{ margin: '1.5rem 0 1rem' }}>
              <p style={{ color: '#b8d8f0', fontSize: '0.92rem', lineHeight: 1.5, margin: '0 0 0.5rem' }}>
                We reserve the right to reject any request for being hateful or inappropriate.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input
                  type="checkbox"
                  id="flash-disclaimer"
                  checked={disclaimerChecked}
                  onChange={e => setDisclaimerChecked(e.target.checked)}
                  style={{ accentColor: BRAND.gold, width: '22px', height: '22px', flexShrink: 0, cursor: 'pointer' }}
                />
                <label htmlFor="flash-disclaimer" style={{ color: '#ffdaad', fontSize: '0.92rem', cursor: 'pointer' }}>
                  I understand.
                </label>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: '1rem',
              borderRadius: '14px',
              border: 'none',
              fontSize: '1.15rem',
              fontWeight: 'bold',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              background: canSubmit ? BRAND.pink : 'rgba(255,255,255,0.12)',
              color:      canSubmit ? 'white'    : 'rgba(255,255,255,0.35)',
              marginTop: '0.5rem',
              transition: 'background 0.2s ease, color 0.2s ease',
            }}
          >
            {submitting ? 'Sending to Dan…' : 'Send it to Dan ✦'}
          </button>

        </div>
      )}

      {onSwitchToCustom && (
        <div style={{ padding: '0 1.5rem 2.5rem', textAlign: 'center' }}>
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', marginBottom: '1.5rem' }} />
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.95rem', margin: '0 0 0.75rem' }}>
            Want something more personal?
          </p>
          <button
            onClick={onSwitchToCustom}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(74,164,225,0.35)',
              borderRadius: '12px',
              padding: '0.9rem 1.5rem',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              width: '100%',
              maxWidth: '360px',
            }}
          >
            📷 Bring your own photo
          </button>
        </div>
      )}
    </>
  );
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const labelStyle = {
  display: 'block',
  color: 'white',
  fontSize: '1rem',
  fontWeight: 'bold',
  marginBottom: '0.4rem',
};

const inputStyle = {
  width: '100%',
  padding: '0.85rem',
  borderRadius: '10px',
  border: '1px solid rgba(74,164,225,0.5)',
  background: 'rgba(255,255,255,0.1)',
  color: 'white',
  fontSize: '1.1rem',
  boxSizing: 'border-box',
};

function counterStyle(max, current) {
  const remaining = max - current;
  return {
    textAlign: 'right',
    fontSize: '0.85rem',
    marginTop: '0.25rem',
    color: remaining < (max * 0.15) ? '#ff8888' : 'rgba(255,255,255,0.45)',
  };
}
